import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { ManagementApiService } from '../management-api.service';
import { ManagementStoreService } from '../management-store.service';
import { AddItemTypeDialogComponent } from '@emm/shared/dialogs/add-item-type-dialog/add-item-type-dialog.component';
import {ItemTypesActions} from '@emm/management/store/item-types/item-types.actions';
import {isGroupItemType} from '@emm/core/models/item-type.model';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';

@Injectable({
    providedIn: 'root'
})
export class ItemTypesEffects {

    loadAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.loadAll),
            switchMap(() =>
                this.api.getAllItemTypes().pipe(
                    map((itemTypes) => ItemTypesActions.loadAllSuccess({ itemTypes })),
                    catchError(({ error }) => of(ItemTypesActions.loadAllFailure({ error })))
                )
            )
        )
    );

    loadAllForItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.loadAllForItems),
            switchMap(() =>
                this.api.getAllLegacyItemTypes().pipe(
                    map((itemTypes) => ItemTypesActions.loadAllForItemsSuccess({ itemTypes })),
                    catchError(({ error }) => of(ItemTypesActions.loadAllForItemsFailure({ error })))
                )
            )
        )
    );

    loadAllForItemGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.loadAllForItemGroups),
            switchMap(() =>
                this.api.getAllLegacyItemTypes().pipe(
                    map((itemTypes) => ItemTypesActions.loadAllForItemGroupsSuccess({
                        itemTypes: itemTypes.filter(isGroupItemType)
                    })),
                    catchError(({ error }) => of(ItemTypesActions.loadAllForItemGroupsFailure({ error })))
                )
            )
        )
    );

    openAddDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.openAddDialog),
            withLatestFrom(this.store.queryItemTypeClasses.all$),
            filter(([_, itemTypeClasses]) => RA.isNonEmptyArray(itemTypeClasses)),
            switchMap(([_, itemTypeClasses]) => {
                const dialogRef = this.dialog.open(AddItemTypeDialogComponent, {
                    width: '600px',
                    data: { showDelete: false, itemTypeClasses },
                });

                return dialogRef.afterClosed().pipe(
                    filter((result) => result && result === 'submit'),
                    tap(() => {
                        this.notificationService.success('Added Successfully');
                    }),
                    map(() => ItemTypesActions.loadAll())
                );
            })
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.delete),
            switchMap(({ itemType }) =>
                this.dialogService
                    .openConfirmDialog('Are you sure you want to delete', itemType.name)
                    .afterClosed()
                    .pipe(
                        switchMap((confirmDelete) =>
                            confirmDelete
                                ? this.api.deleteItemType(itemType.tag).pipe(
                                map(() => ItemTypesActions.deleteSuccess()),
                                catchError(({ error }) => of(ItemTypesActions.deleteFailure({ error })))
                                )
                                : of(ItemTypesActions.deleteFailure({ error: 'No Clicked' }))
                        )
                    )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemTypesActions.deleteSuccess),
            tap(() => {
                this.notificationService.success('Deleted successfully');
            }),
            switchMap(() => of(ItemTypesActions.loadAll()))
        )
    );

    constructor(
        private actions$: Actions,
        private api: ManagementApiService,
        public dialog: MatDialog,
        private dialogService: DialogService,
        private notificationService: NotificationService,
        private store: ManagementStoreService
    ) {}

}
